import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMigrationPreview } from '@/hooks/useMigrationPreview';
import { MigrationComparisonView } from '@/components/migration/MigrationComparisonView';
import { MigrationStrategySelector } from '@/components/migration/MigrationStrategySelector';
import { migrateObject } from '@/lib/stores/objectsStore';
import { Button } from '@/components/ui/button';
import { IconProvider } from '@/components/ui';
import { LoadingSpinner } from '@/components/loaders/loading-spinner';
import { useToast } from '@/hooks/use-toast';
import { useConfirmation } from '@/hooks/use-confirmation';

export default function ObjectMigrationPage() {
  const { objectId } = useParams<{ objectId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { confirmation } = useConfirmation();

  const [selectedStrategy, setSelectedStrategy] = useState<'merge' | 'overwrite'>('merge');
  const [isMigrating, setIsMigrating] = useState(false);

  const { previewData, isLoading, error } = useMigrationPreview(objectId);

  const handleCancel = () => {
    navigate(`/library/${objectId}`);
  };

  const handleMigrate = async () => {
    if (!previewData) return;

    const hasStructuralChanges = previewData.preview.changes.hasChanges;
    const strategyLabel = selectedStrategy === 'merge' ? 'Merge' : 'Overwrite';
    
    let warningMessage: string;
    let confirmText: string;
    
    if (!hasStructuralChanges) {
      // No structural changes, just version update
      warningMessage = 'This will update the object\'s blueprint version to match the latest template version. No data will be modified.';
      confirmText = 'Update Version';
    } else if (selectedStrategy === 'overwrite') {
      warningMessage = 'This will RESET the object to the new blueprint structure and LOSE all existing data. This cannot be undone.';
      confirmText = 'Apply Migration';
    } else {
      warningMessage = 'This will update the object structure while preserving your data. New fields will be added with default values.';
      confirmText = 'Apply Migration';
    }

    const confirmed = await confirmation({
      title: hasStructuralChanges ? `Apply ${strategyLabel} Migration?` : 'Update Blueprint Version?',
      description: warningMessage,
      confirmText: confirmText,
      cancelText: 'Cancel',
      variant: selectedStrategy === 'overwrite' && hasStructuralChanges ? 'destructive' : 'default',
    });

    if (!confirmed) return;

    setIsMigrating(true);

    try {
      await migrateObject(
        previewData.object.id,
        previewData.preview.targetVersion,
        selectedStrategy,
        toast
      );

      toast({
        title: 'Success',
        description: 'Object migrated successfully',
      });

      // Navigate back to the object page
      navigate(`/library/${objectId}`);
    } catch (err: any) {
      console.error('Migration failed:', err);
      toast({
        title: 'Error',
        description: err.message || 'Failed to migrate object',
        variant: 'destructive',
      });
    } finally {
      setIsMigrating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" text="Loading migration preview..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <IconProvider iconstring="fa-exclamation-triangle" size={48} color="#ef4444" />
          <h2 className="text-xl font-semibold text-red-600 dark:text-red-400">
            Failed to Load Migration Preview
          </h2>
          <p className="text-gray-600 dark:text-gray-400">{error}</p>
          <Button onClick={handleCancel} variant="outline">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  if (!previewData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <IconProvider iconstring="fa-question-circle" size={48} color="#6b7280" />
          <h2 className="text-xl font-semibold text-gray-600 dark:text-gray-400">
            No Preview Data Available
          </h2>
          <Button onClick={handleCancel} variant="outline">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          className="mb-4"
        >
          <IconProvider iconstring="fa-arrow-left" size={14} className="mr-2" />
          Back to Object
        </Button>

        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {previewData.object.name}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Migration from v{previewData.preview.currentVersion.major}.
          {previewData.preview.currentVersion.minor}.{previewData.preview.currentVersion.patch} to
          v{previewData.preview.targetVersion.major}.{previewData.preview.targetVersion.minor}.
          {previewData.preview.targetVersion.patch}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Comparison View */}
        <div className="lg:col-span-2">
          <MigrationComparisonView
            currentStructure={previewData.object.data}
            changes={previewData.preview.changes}
            blueprintName={previewData.blueprint.name}
            currentVersion={previewData.preview.currentVersion}
            targetVersion={previewData.preview.targetVersion}
          />
        </div>

        {/* Sidebar with Strategy and Actions */}
        <div className="lg:col-span-1">
          <div className="sticky top-4 space-y-4">
            {/* Strategy Selector */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <MigrationStrategySelector
                selectedStrategy={selectedStrategy}
                onStrategyChange={setSelectedStrategy}
              />
            </div>

            {/* Action Buttons */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Actions
              </h3>
              <div className="space-y-2">
                <Button
                  onClick={handleMigrate}
                  disabled={isMigrating}
                  className="w-full"
                  variant={selectedStrategy === 'overwrite' ? 'destructive' : 'default'}
                >
                  {isMigrating ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Migrating...
                    </>
                  ) : (
                    <>
                      <IconProvider iconstring="fa-check" size={14} className="mr-2" />
                      {previewData.preview.changes.hasChanges ? 'Apply Migration' : 'Update Version'}
                    </>
                  )}
                </Button>
                <Button onClick={handleCancel} variant="outline" className="w-full">
                  <IconProvider iconstring="fa-times" size={14} className="mr-2" />
                  Cancel
                </Button>
              </div>

              {!previewData.preview.changes.hasChanges && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 text-center">
                  No structural changes. Will update version only.
                </p>
              )}
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <IconProvider iconstring="fa-info-circle" size={16} color="#3b82f6" />
                <div className="text-sm text-blue-800 dark:text-blue-400">
                  <p className="font-medium mb-1">Migration Tips</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>Review all changes carefully before applying</li>
                    <li>Use "Merge" to keep existing data</li>
                    <li>Use "Overwrite" only if you want to start fresh</li>
                    <li>Migrations can be reverted using version history</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

